import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { FieldData, formattedCurrency, sanitizeToNumeric, useForm } from 'vopay';
import {
  PermissionFieldRenderer,
  PermissionItem,
  PermissionSection,
  PermissionSectionStats,
  PermissionValues,
  PermissionsEditorConfig,
} from './types';

// The headless permissions engine. Wraps vopay `useForm` and owns everything the account and partner
// drawers each reimplement today: grouping/sort, dependency show/hide, search, enable/disable-all
// (global + per-section), unsaved-changes + validity, and a save payload that carries only the items a
// dependency currently makes visible. It renders nothing — the field control ([2]'s UI) is injected as
// `renderField`, and the derived `visibleSections` tree is what [2] lays out.
//
// Named `usePermissionsEditor`, not `usePermissions`: both portals already have a role-permission
// `usePermissions` hook.

type UsePermissionsEditorParams = {
  /** Normalized sections, already adapted from the raw response and label-baked by the host. */
  sections: PermissionSection[];
  /** Receives the serialized values of the dependency-visible items only. */
  onSave: (values: PermissionValues) => void;
  /** How the consumer renders one item's control; forwarded into each field's `useForm` config. */
  renderField: PermissionFieldRenderer;
  config?: PermissionsEditorConfig;
};

/** Depth-first over sub-sections and toggle children — every item that carries a form value. */
function flattenItems(sections: PermissionSection[]): PermissionItem[] {
  const items: PermissionItem[] = [];

  const walkItems = (list: PermissionItem[]) => {
    list.forEach((item) => {
      items.push(item);
      walkItems(item.children);
    });
  };

  const walkSections = (list: PermissionSection[]) => {
    list.forEach((section) => {
      walkItems(section.items);
      walkSections(section.sections);
    });
  };

  walkSections(sections);

  return items;
}

/** OR-semantics: no dependencies means always visible; otherwise any one listed code being on is enough. */
function isDependencyVisible(item: PermissionItem, values: PermissionValues): boolean {
  if (item.dependencies.length === 0) return true;

  return item.dependencies.some((code) => values[code] === true);
}

/** A number field's raw string value parses to a finite number (after stripping the thousands commas). */
function toFiniteNumber(value: PermissionValues[string]): number | null {
  const parsed = Number(sanitizeToNumeric(String(value)));

  return Number.isFinite(parsed) ? parsed : null;
}

function buildInitialValues(items: PermissionItem[]): PermissionValues {
  const values: PermissionValues = {};

  items.forEach((item) => {
    if (item.type === 'boolean') {
      values[item.code] = Boolean(item.value);
    } else if (item.type === 'number') {
      // Currency amounts are pre-formatted on load (mirrors AccountPermissionForm.tsx); the currency
      // symbol is a render concern, so formatting the number needs no currency code.
      values[item.code] = item.isCurrency ? formattedCurrency(String(item.value ?? 0)) : String(item.value ?? 0);
    } else {
      values[item.code] = String(item.value ?? '');
    }
  });

  return values;
}

export default function usePermissionsEditor({
  sections,
  onSave,
  renderField,
  config = {},
}: UsePermissionsEditorParams) {
  const [searchTerm, setSearchTerm] = useState('');

  const flatItems = useMemo(() => flattenItems(sections), [sections]);
  const initialValues = useMemo(() => buildInitialValues(flatItems), [flatItems]);

  const fieldConfig = useMemo<FieldData<PermissionValues>[]>(
    () =>
      flatItems.map((item) => ({
        fieldName: item.code,
        getInputState: () => 'optional',
        // Number fields hold their value as a raw string; reject anything that doesn't parse to a finite
        // number (e.g. '$500', '1 000', 'abc') so `isValid` blocks the save instead of posting NaN. Empty
        // is allowed (optional) and serializes to 0 — clearing a limit means "no limit". The message is
        // English-only: shared holds no permission translations (ID-13342), and [2] owns error display.
        // A dependency-hidden field isn't validated, so a hidden bad amount can't block the save.
        validate:
          item.type === 'number'
            ? (formCtx) =>
                isDependencyVisible(item, formCtx.values)
                  ? Yup.string().test(
                      'is-finite-number',
                      'Enter a valid amount',
                      (value) => value === undefined || value === '' || toFiniteNumber(value) !== null,
                    )
                  : Yup.mixed()
            : undefined,
        // Always shown to `useForm`: which rows exist is decided by `visibleSections`, and a row that a
        // dependency just hid must still render while it animates out (`useForm` renders nothing for a
        // `showField:false` field). Dependency visibility is applied to validation above and to the save.
        showField: () => true,
        serializeField: (values) =>
          item.type === 'number' ? (toFiniteNumber(values[item.code]) ?? 0) : values[item.code],
        renderField: (arg) => renderField(item, arg),
      })),
    [flatItems, renderField],
  );

  const configByCode = useMemo(
    () => new Map(fieldConfig.map((field) => [String(field.fieldName), field])),
    [fieldConfig],
  );

  const { ctx, renderFormElements } = useForm<PermissionValues, Partial<PermissionValues>>({
    config: fieldConfig,
    initialValues,
    // After a save the host refetches and passes fresh `sections`; reseed the form to that as the new
    // clean baseline (so `hasChanges` clears) instead of keeping the pre-save edits. Also lets the host
    // swap accounts without remounting. Only reseeds when `initialValues` changes (memoized on `sections`).
    enableReinitialize: true,
    // Only dependency-visible items are saved — never gated by search, so a search-hidden item still is.
    onSubmit: (values, { values: formValues }) => {
      const visibleValues: PermissionValues = {};

      flatItems.forEach((item) => {
        const value = values[item.code];

        if (value !== undefined && isDependencyVisible(item, formValues)) visibleValues[item.code] = value;
      });

      onSave(visibleValues);
    },
  });

  const sortSections = useMemo(
    () =>
      (list: PermissionSection[]): PermissionSection[] =>
        config.sort ? [...list].sort(config.sort) : list,
    [config.sort],
  );

  const isFiltering = searchTerm.trim().length > 0;

  // The tree [2] renders: dependency-hidden items removed, search applied (a section-label match keeps the
  // whole subtree — its items and every descendant section — otherwise only items whose own label matches),
  // empties pruned, sub-sections sorted.
  const visibleSections = useMemo(() => {
    const lowerSearch = searchTerm.trim().toLowerCase();

    const itemMatchesSearch = (item: PermissionItem): boolean =>
      item.label.toLowerCase().includes(lowerSearch) || item.children.some(itemMatchesSearch);

    // `ancestorMatched` carries a matching parent's decision down: once any section on the path matches the
    // search, itself and its whole subtree are kept, so matching a branch section's own label doesn't prune
    // its descendants (each of which has a different label) and leave the branch empty.
    const filterSection = (section: PermissionSection, ancestorMatched: boolean): PermissionSection | null => {
      const sectionMatches = ancestorMatched || !isFiltering || section.label.toLowerCase().includes(lowerSearch);

      const items = section.items
        .filter((item) => isDependencyVisible(item, ctx.values))
        .filter((item) => sectionMatches || itemMatchesSearch(item))
        .map((item) => ({
          ...item,
          children: item.children.filter((child) => isDependencyVisible(child, ctx.values)),
        }));

      const subSections = sortSections(
        section.sections
          .map((subSection) => filterSection(subSection, sectionMatches))
          .filter((s): s is PermissionSection => s !== null),
      );

      if (items.length === 0 && subSections.length === 0) return null;

      return { ...section, items, sections: subSections };
    };

    return sortSections(
      sections.map((section) => filterSection(section, false)).filter((s): s is PermissionSection => s !== null),
    );
  }, [sections, searchTerm, isFiltering, ctx.values, sortSections]);

  // Dependency-visible items in a section (recursive), for counts and enable/disable-all. The UI passes a
  // `visibleSections` node, so while searching the counts cover only the matching rows — as the current
  // drawers do; the per-section buttons are hidden then.
  const collectVisibleItems = (section: PermissionSection): PermissionItem[] => {
    const items: PermissionItem[] = [];

    const push = (item: PermissionItem) => {
      if (!isDependencyVisible(item, ctx.values)) return;
      items.push(item);
      item.children.forEach(push);
    };

    section.items.forEach(push);
    section.sections.forEach((sub) => items.push(...collectVisibleItems(sub)));

    return items;
  };

  const getSectionStats = (section: PermissionSection): PermissionSectionStats => {
    const booleans = collectVisibleItems(section).filter((item) => item.type === 'boolean');

    return {
      enabled: booleans.filter((item) => ctx.values[item.code] === true).length,
      total: booleans.length,
    };
  };

  const setBooleans = (items: PermissionItem[], value: boolean) => {
    const updates: PermissionValues = {};

    items.forEach((item) => {
      if (item.type === 'boolean') updates[item.code] = value;
    });

    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Global enable/disable spans every toggle regardless of visibility (matches the drawer's "enable all");
  // the per-section variants act on that section's dependency-visible toggles only.
  const enableAll = () => setBooleans(flatItems, true);
  const disableAll = () => setBooleans(flatItems, false);
  const enableSection = (section: PermissionSection) => setBooleans(collectVisibleItems(section), true);
  const disableSection = (section: PermissionSection) => setBooleans(collectVisibleItems(section), false);

  const hasChanges = useMemo(
    () => JSON.stringify(ctx.values) !== JSON.stringify(initialValues),
    [ctx.values, initialValues],
  );

  return {
    ctx,
    renderFormElements,
    /** The `useForm` field config for one item's code — pass to `renderFormElements({ config: [...] })`. */
    getFieldConfig: (code: string) => configByCode.get(code),
    visibleSections,
    searchTerm,
    setSearchTerm,
    isFiltering,
    hasResults: visibleSections.length > 0,
    enableAll,
    disableAll,
    enableSection,
    disableSection,
    getSectionStats,
    hasChanges,
    isValid: ctx.isValid,
  };
}
